import React from 'react';
import '../styles/FuturesPanel.css';

const FuturesPanel = ({ gammaData }) => {
  if (!gammaData || !gammaData.hasValidData) return null;

  const {
    spotPrice,
    syntheticFutures,
    futuresBasis,
    futuresBasisPct,
    futuresFairCarryPct,
    futuresExcessBasisPct,
    futuresBasisDeviationPct,
    futuresBasisState,
    futuresTradeSignal,
  } = gammaData;

  if (syntheticFutures == null) return null;

  const fmt  = v => v != null ? v.toFixed(2) : '—';
  const fmtI = v => v != null ? Math.round(v).toLocaleString('en-IN') : '—';
  const sign = v => v != null && v >= 0 ? '+' : '';

  const basisColor = futuresBasisState === 'PREMIUM'  ? '#00c853'
                   : futuresBasisState === 'DISCOUNT' ? '#ef5350'
                   : '#9e9e9e';

  const signalColor = futuresTradeSignal === 'BUY CE' ? '#00c853'
                    : futuresTradeSignal === 'BUY PE' ? '#ef5350'
                    : '#666';

  const guideText = (() => {
    // State is basis vs fair cost of carry (r − q)·T, NOT raw basis — an index future always
    // trades above spot by carry, so a raw premium says nothing about direction.
    if (futuresBasisState === 'PREMIUM')  return '↑ Futures rich vs fair carry — informational; signal comes from the basis moving vs its 60-min baseline';
    if (futuresBasisState === 'DISCOUNT') return '↓ Futures cheap vs fair carry — informational; signal comes from the basis moving vs its 60-min baseline';
    return '→ Futures near fair carry';
  })();

  return (
    <div className="fp-panel">
      <div className="fp-header">
        <span className="fp-title">Synthetic Futures</span>
        <span className="fp-basis-badge" style={{ background: basisColor }}>
          {futuresBasisState}
        </span>
      </div>

      {/* Trade signal row */}
      <div className="fp-signal-row" style={{ borderColor: signalColor }}>
        <span className="fp-signal-label">Futures Signal</span>
        <span className="fp-signal-value" style={{ color: signalColor }}>
          {futuresTradeSignal === 'BUY CE' && '▲ BUY CE — basis widening vs 60-min baseline (futures bid up)'}
          {futuresTradeSignal === 'BUY PE' && '▼ BUY PE — basis narrowing vs 60-min baseline (futures offered)'}
          {futuresTradeSignal === 'NO SIGNAL' && '— Basis steady vs baseline (or baseline still forming)'}
        </span>
      </div>

      <div className="fp-grid">
        <div className="fp-item">
          <span className="fp-label">Synthetic F</span>
          <span className="fp-value">{fmtI(syntheticFutures)}</span>
        </div>

        <div className="fp-item">
          <span className="fp-label">Spot</span>
          <span className="fp-value">{fmtI(spotPrice)}</span>
        </div>

        <div className="fp-item">
          <span className="fp-label">Basis (F − Spot)</span>
          <span className="fp-value" style={{ color: basisColor }}>
            {sign(futuresBasis)}{fmt(futuresBasis)}
          </span>
        </div>

        <div className="fp-item">
          <span className="fp-label">Basis %</span>
          <span className="fp-value" style={{ color: basisColor }}>
            {sign(futuresBasisPct)}{futuresBasisPct?.toFixed(3)}%
          </span>
        </div>

        <div className="fp-item">
          <span className="fp-label">Fair Carry %</span>
          <span className="fp-value">{sign(futuresFairCarryPct)}{futuresFairCarryPct?.toFixed(3) ?? '—'}%</span>
        </div>

        <div className="fp-item">
          <span className="fp-label">Excess / Δ Baseline</span>
          <span className="fp-value" style={{ color: signalColor }}>
            {sign(futuresExcessBasisPct)}{futuresExcessBasisPct?.toFixed(3) ?? '—'}%
            {' / '}
            {futuresBasisDeviationPct != null ? `${sign(futuresBasisDeviationPct)}${futuresBasisDeviationPct.toFixed(3)}%` : 'forming'}
          </span>
        </div>
      </div>

      <div className="fp-guide">{guideText}</div>
    </div>
  );
};

export default FuturesPanel;
